"use client";

import { Check, ChevronDown, History } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface VersionSelectorProps {
  history: { versionNumber: number; createdAt: string | Date }[];
  currentVersion: number;
  latestVersion: number;
  onChange: (versionNumber: number) => void;
  className?: string;
}

function formatDate(value: string | Date): string {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Compact version pill. Hidden entirely when only one version exists, so
 * versioning never dominates the interface.
 */
export function VersionSelector({
  history,
  currentVersion,
  latestVersion,
  onChange,
  className,
}: VersionSelectorProps) {
  if (history.length <= 1) return null;

  const isLatest = currentVersion === latestVersion;
  const ordered = [...history].sort((a, b) => b.versionNumber - a.versionNumber);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Version ${currentVersion}${isLatest ? " (latest)" : " (older version)"}. Choose a version`}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-full border pr-2 pl-2.5 text-[12.5px] font-medium transition-colors",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            isLatest
              ? "border-line-strong bg-surface-1 text-ink-2 hover:border-line-accent hover:text-ink"
              : "border-[rgb(232_184_109/0.45)] bg-[rgb(232_184_109/0.1)] text-ember hover:bg-[rgb(232_184_109/0.16)]",
            className
          )}
        >
          <History className="size-3.5" />
          <span className="font-mono">v{currentVersion}</span>
          <span className={cn("text-[11px]", isLatest ? "text-ink-3" : "text-ember/90")}>
            {isLatest ? "Latest" : "Older"}
          </span>
          <ChevronDown className="size-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Version history</DropdownMenuLabel>
        {ordered.map((v) => {
          const active = v.versionNumber === currentVersion;
          const latest = v.versionNumber === latestVersion;
          return (
            <DropdownMenuItem
              key={v.versionNumber}
              onSelect={() => onChange(v.versionNumber)}
              className={cn("items-start gap-2.5 py-2", active && "text-ink")}
            >
              <span className="mt-0.5 flex size-3.5 shrink-0 items-center justify-center text-accent">
                {active && <Check className="size-3.5" />}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-[12.5px]">v{v.versionNumber}</span>
                  {latest && (
                    <span className="rounded-full bg-accent-tint px-1.5 py-px text-[10px] font-medium uppercase tracking-wider text-accent">
                      Latest
                    </span>
                  )}
                </span>
                <span className="text-[11.5px] text-ink-3">{formatDate(v.createdAt)}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Shown above content when an older version is displayed.
 */
export function HistoricalNotice({
  currentVersion,
  latestVersion,
  onReturn,
  className,
}: {
  currentVersion: number;
  latestVersion: number;
  onReturn: () => void;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-[rgb(232_184_109/0.35)] bg-[rgb(232_184_109/0.08)] px-3.5 py-2.5 text-[13px] text-[#f3d9a4]",
        className
      )}
    >
      <History className="size-4 shrink-0 text-ember" />
      <span>
        You are reading <span className="font-mono">v{currentVersion}</span>, an older version of
        this Essence.
      </span>
      <button
        type="button"
        onClick={onReturn}
        className="ml-auto rounded-md font-medium text-ember underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ember"
      >
        Read latest (v{latestVersion})
      </button>
    </div>
  );
}
